import { mapActions, mapGetters, mapState } from "vuex";
import { pluralCount } from "@/helpers/helpers";
import { PERIOD } from "@/enums/period.enum";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import moment from "moment";
import { FORMAT } from "@/config/date-format";
const DATA_KEYS = {
  BASIC: "basic",
  COMPARISON: "comparison"
};
export const categoryAnalyticsCommonMixin = {
  mixins: [gtmEngagementMixin],
  data() {
    return {
      summary: {
        averageRatings: {
          basic: null,
          comparison: null
        },
        total: {
          basic: null,
          comparison: null
        }
      },
      heatmap: {
        isLoading: false,
        basic: [],
        comparison: []
      }
    };
  },
  computed: {
    ...mapGetters("reviewsCategoriesAnalytics", [
      "getBasicParams",
      "getComparisonParams"
    ]),
    ...mapState("reviewsCategoriesAnalytics", [
      "selectedDate",
      "isComparing",
      "selectedFacility",
      "comparisonDate",
      "isAutoCategorized"
    ]),
    isAutoCategorizedParam() {
      return {
        is_auto_categorized: this.isAutoCategorized || undefined
      };
    }
  },
  mounted() {
    this.mixin__setGtmEngagementTimeouts();
  },
  methods: {
    ...mapActions("reviewsCategoriesAnalytics", [
      "setComparisonDate",
      "setSelectedDate",
      "setIsComparing",
      "setSelectedFacility",
      "clearSelectedFacility",
      "clearComparison",
      "setIsAutoCategorized"
    ]),

    async prepareInitialData() {
      this.clearSelectedFacility();
      this.initValues();
      await this.getPageData();
    },
    async getSummaryData(params, dataKey) {
      const { averageRatings, total } = await this.fetchSummaryData(params);
      this.summary.averageRatings[dataKey] = averageRatings;
      this.summary.total[dataKey] = total;
    },
    async getHeatmapData(payload, dataKey) {
      this.heatmap[dataKey] = await this.fetchHeatmapData({
        ...payload,
        order_by: "count",
        order_direction: "desc"
      });
    },
    async onAutoCategorizedFilterChange(showOnlyCategorized) {
      this.setIsAutoCategorized(showOnlyCategorized);
      const queries = { ...this.$route.query };
      delete queries["is_auto_categorized"];

      await this.$router.push({
        query: {
          ...queries,
          ...(showOnlyCategorized && {
            is_auto_categorized: showOnlyCategorized
          })
        }
      });
      this.clearHeatmapData();

      if (this.isComparing) {
        await this.getHeatmapWithComparison();
      } else {
        await this.getHeatmapBasic();
      }
    },
    getHeatmapWithComparison() {
      return Promise.all([this.getHeatmapBasic(), this.getHeatmapComparison()]);
    },
    getHeatmapComparison() {
      return this.getHeatmapData(
        { ...this.comparisonParams, ...this.isAutoCategorizedParam },
        DATA_KEYS.COMPARISON
      );
    },
    getHeatmapBasic() {
      return this.getHeatmapData(
        { ...this.basicParams, ...this.isAutoCategorizedParam },
        DATA_KEYS.BASIC
      );
    },
    clearHeatmapData() {
      this.heatmap.isLoading = false;
      this.heatmap.basic = [];
      this.heatmap.comparison = [];
    },
    clearSummaryData() {
      this.summary.averageRatings.basic = null;
      this.summary.averageRatings.comparison = null;
      this.summary.total.basic = null;
      this.summary.total.comparison = null;
    },
    async fetchCoreData() {
      this.clearHeatmapData();
      this.clearSummaryData();
      if (this.isComparing) {
        await Promise.all([
          this.getPrimaryBasicData(),
          this.getPrimaryComparisonData()
        ]);
      } else {
        await this.getPrimaryBasicData();
      }
    },
    async getPrimaryComparisonData() {
      await Promise.all([
        this.getHeatmapData(
          { ...this.comparisonParams, ...this.isAutoCategorizedParam },
          DATA_KEYS.COMPARISON
        ),
        this.getSummaryData(this.comparisonParams, DATA_KEYS.COMPARISON)
      ]);
    },
    async getPrimaryBasicData() {
      await Promise.all([
        this.getSummaryData(this.basicParams, DATA_KEYS.BASIC),
        this.getHeatmapData(
          { ...this.basicParams, ...this.isAutoCategorizedParam },
          DATA_KEYS.BASIC
        )
      ]);
    },

    setDefaultDate() {
      const startDate = moment(Date.now()).subtract(30, "days");
      const endDate = moment(Date.now());
      this.setSelectedDate({
        date_from: startDate.format(FORMAT.DATE),
        date_to: endDate.format(FORMAT.DATE)
      });
      this.$router.push({
        query: {
          ...this.$route.query,
          selectedFrom: this.selectedDate.date_from,
          selectedTo: this.selectedDate.date_to
        }
      });
    },

    getFacilitiesCountLabel(count) {
      return count > 0
        ? `(${pluralCount("message.pluralForms.facility", count)})`
        : "";
    },

    async selectFacility(facility) {
      this.setFacilitySelectData(facility);
      await this.getPageData();
    },
    toggleHeatmapScreen() {
      this.isChartFullScreen = !this.isChartFullScreen;
    },
    async changeDate({ date_from, date_to, comparison, period_type }) {
      this.setDateChangeData(date_from, date_to, comparison, period_type);
      await this.getPageData();
    },
    async initSelectedFacility() {
      const { facility, facility_group, city, facilityGmb } = this.$route.query;
      if (facility || facility_group || city) {
        this.setSelectedFacility({
          tab: +this.$route.query.facility_tab,
          id: this.$route.query.facility,
          params: { facility, facilityGmb, facility_group, city },
          meta: {
            displayName: this.$route.query.displayName,
            address: this.$route.query.address,
            storeCode: this.$route.query.storeCode,
            quantity: this.$route.query.quantity
          }
        });
      }
    },

    async initSelectedDate() {
      const { selectedFrom, selectedTo, dateType } = this.$route.query;

      if (selectedFrom && selectedTo) {
        this.setSelectedDate({
          date_from: selectedFrom,
          date_to: selectedTo
        });
      } else if (dateType !== PERIOD.FULL) {
        this.setDefaultDate();
      }
    },

    async initComparisonDate() {
      if (this.$route.query.comparisonFrom && this.$route.query.comparisonTo) {
        this.setIsComparing(true);
        this.setComparisonDate({
          date_from: this.$route.query.comparisonFrom,
          date_to: this.$route.query.comparisonTo
        });
      }
    },

    initValues() {
      this.initSelectedFacility();
      this.initSelectedDate();
      this.initComparisonDate();
      this.initIsAutoCategorized();
    },
    initIsAutoCategorized() {
      this.setIsAutoCategorized(this.$route.query.is_auto_categorized);
    },
    async setFacilitySelectData(facility) {
      const queries = { ...this.$route.query };
      delete queries["categoryId"];
      delete queries["facility"];
      delete queries["facilityGmb"];
      delete queries["city"];
      delete queries["facility_group"];
      delete queries["withoutCategory"];
      const { params, meta, id, tab } = facility;
      this.$router.replace({
        query: {
          ...queries,
          ...params,
          ...meta,
          facility_tab: tab
        }
      });
      if (!id) {
        this.clearSelectedFacility();
        this.facilitiesDialog = false;
      }
      this.setSelectedFacility(facility);
    },

    setSelectedDateValue(date_from, date_to) {
      const selectedDateValue =
        date_from && date_to ? { date_from, date_to } : null;
      this.setSelectedDate(selectedDateValue);
    },

    setComparisonDateValue(comparison) {
      const comparisonDateValue = comparison
        ? { date_from: comparison.date_from, date_to: comparison.date_to }
        : null;
      this.setComparisonDate(comparisonDateValue);
    },

    updateRouteQuery(selectedDateValue) {
      const queries = { ...this.$route.query };

      if (!selectedDateValue) {
        delete queries.selectedFrom;
        delete queries.selectedTo;
      }

      if (!this.comparisonDate) {
        this.clearComparison();
        delete queries.compareOption;
        delete queries.comparisonFrom;
        delete queries.comparisonTo;
      }

      this.$router.push({
        query: {
          ...queries,
          ...(selectedDateValue && {
            selectedFrom: this.selectedDate.date_from,
            selectedTo: this.selectedDate.date_to
          }),
          ...(this.comparisonDate && {
            comparisonFrom: this.comparisonDate.date_from,
            comparisonTo: this.comparisonDate.date_to
          })
        }
      });
    },

    setDateChangeData(date_from, date_to, comparison) {
      this.setSelectedDateValue(date_from, date_to);
      this.setComparisonDateValue(comparison);
      this.updateRouteQuery(this.selectedDate, this.comparisonDate);
    }
  },
  beforeDestroy() {
    this.$store.dispatch("reviewsCategoriesAnalytics/clearComparison");
  }
};
